// React hook wrapping the Matrix search wrapper.
//
// Debounces the `query` input by 300ms before firing a request, guards
// stale responses with a monotonic token (`latestQueryRef`), and
// exposes `searchMore` for pagination. Errors are surfaced as `null`
// for the caller — the UI renders the empty state, which is the
// documented fail-silent contract (a failed lookup looks the same as
// "no results" to the user; a retry is one keypress away).
//
// The `search` function is injected so the panel can be rendered in
// stories with a fake backend. A production caller passes
// `(query, roomId, nextBatch) => searchEvents(client, query, roomId, nextBatch)`.
//
// The hook does not normalise the query — leading / trailing whitespace
// is forwarded as typed. `searchEvents` short-circuits empty queries
// downstream and would forward the exact bytes to a real homeserver.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { SearchResult, SearchResultsPage } from "~/lib/matrix";

export type SearchFunction = (
  query: string,
  roomId?: string,
  nextBatch?: string,
) => Promise<SearchResultsPage>;

export interface UseSearchResult {
  /** Matches accumulated across pages. Re-queries (new debounce tick)
   *  replace the list; `searchMore` appends. */
  results: SearchResult[];
  /** True while a request is in flight — including the first fire
   *  after the debounce window. */
  isSearching: boolean;
  /** Opaque cursor from the homeserver; `null` once exhausted. */
  nextBatch: string | null;
  /** Fetch the next page. No-op while a request is in flight, or when
   *  the cursor is exhausted. Returns the page so callers that want
   *  to react to pagination (scroll lock, analytics) can await it. */
  searchMore: () => Promise<void>;
}

const DEBOUNCE_MS = 300;

export function useSearch(search: SearchFunction, query: string, roomId?: string): UseSearchResult {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [nextBatch, setNextBatch] = useState<string | null>(null);

  // Monotonic request token. Each new debounce tick bumps the token;
  // responses apply only when their token matches the current one.
  // This is the stale-response guard recommended in every async hook
  // primer — and a necessity when typing at full speed debounces to
  // several near-simultaneous requests.
  const latestQueryTokenRef = useRef(0);
  // Latest query / roomId pair captured on the firing edge of the
  // debounce, plus the token that was current at that moment.
  // `searchMore` guards against mid-debounce races by comparing the
  // stashed token against the current token: if the user typed
  // another character but the new debounce hasn't fired yet,
  // `latestQueryTokenRef.current` already advanced past
  // `issuedToken`, and `searchMore` correctly bails.
  const lastIssuedRef = useRef<{
    query: string;
    roomId: string | undefined;
    token: number;
  } | null>(null);

  // Debounced query lifecycle. An empty / whitespace-only query
  // instantly resets state without a timer — the user has cleared
  // the input and wants the empty prompt back, not a stale snapshot
  // sitting around until the debounce fires.
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      // Bump the token so any in-flight request lands in the "stale"
      // branch and is discarded.
      latestQueryTokenRef.current += 1;
      lastIssuedRef.current = null;
      setResults([]);
      setNextBatch(null);
      setIsSearching(false);
      return;
    }
    const token = ++latestQueryTokenRef.current;
    const timer = setTimeout(async () => {
      lastIssuedRef.current = { query: trimmed, roomId, token };
      setIsSearching(true);
      try {
        const page = await search(trimmed, roomId);
        if (latestQueryTokenRef.current !== token) return;
        setResults(page.results);
        setNextBatch(page.nextBatch);
      } catch {
        if (latestQueryTokenRef.current !== token) return;
        // Fail-silent — the UI treats an empty result list and a
        // failure identically. Logging per failure is the right
        // cadence; the debounce already throttles the issue rate to
        // one request per user-typed word.
        console.warn("useSearch: search failed");
        setResults([]);
        setNextBatch(null);
      } finally {
        if (latestQueryTokenRef.current === token) setIsSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, roomId, search]);

  const searchMore = useCallback(async () => {
    const issued = lastIssuedRef.current;
    if (!issued) return;
    if (nextBatch === null) return;
    if (isSearching) return;
    // Invariant: `nextBatch !== null` is only ever set after a
    // completed first fetch, which is the only path that populates
    // `lastIssuedRef`. So by the time `searchMore` is reachable,
    // `issued` is guaranteed to carry the token of the just-served
    // query — never a token bumped by a still-pending debounce.
    // If a future caller surfaces a Load-more affordance during
    // the first fetch, that invariant breaks and the token check
    // below becomes load-bearing.
    //
    // Bail if the user has typed another character since we last
    // issued — the debounce hasn't fired yet but the token has
    // already advanced, so any page we fetched here would land
    // out-of-order with whatever the new query resolves to.
    if (issued.token !== latestQueryTokenRef.current) return;
    const token = issued.token;
    setIsSearching(true);
    try {
      const page = await search(issued.query, issued.roomId, nextBatch);
      if (latestQueryTokenRef.current !== token) return;
      setResults((current) => [...current, ...page.results]);
      setNextBatch(page.nextBatch);
    } catch {
      if (latestQueryTokenRef.current !== token) return;
      console.warn("useSearch: searchMore failed");
    } finally {
      if (latestQueryTokenRef.current === token) setIsSearching(false);
    }
  }, [isSearching, nextBatch, search]);

  return useMemo<UseSearchResult>(
    () => ({ results, isSearching, nextBatch, searchMore }),
    [results, isSearching, nextBatch, searchMore],
  );
}
